import React, { useContext, useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaTachometerAlt, FaBoxOpen, FaShoppingCart, FaUsers, FaTags,
  FaCog, FaSignOutAlt, FaArrowLeft, FaBars, FaTimes, FaEnvelope,
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import Logo from '../Logo';
import './AdminLayout.css';

const AdminLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const { settings } = useSettings();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUnread = async () => {
      try {
        const res = await axios.get('/api/contact/unread-count');
        setUnreadMessages(res.data.count || 0);
      } catch (e) { /* silent */ }
    };
    loadUnread();
    const interval = setInterval(loadUnread, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { to: '/admin', label: 'Dashboard', icon: FaTachometerAlt, end: true },
    { to: '/admin/products', label: 'Products', icon: FaBoxOpen },
    { to: '/admin/orders', label: 'Orders', icon: FaShoppingCart },
    { to: '/admin/categories', label: 'Categories', icon: FaTags },
    { to: '/admin/messages', label: 'Messages', icon: FaEnvelope, badge: unreadMessages },
    { to: '/admin/users', label: 'Users', icon: FaUsers, adminOnly: true },
    { to: '/admin/settings', label: 'Settings', icon: FaCog, adminOnly: true },
  ];

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-brand">
          <div className="admin-brand-logo">
            <Logo variant="mark" size={36} />
          </div>
          <div>
            <strong>{settings?.siteName || 'Centre Techniques'}</strong>
            <small>Admin panel</small>
          </div>
        </div>

        <nav className="admin-nav">
          {navItems.map(({ to, label, icon: Icon, end, adminOnly, badge }) => {
            if (adminOnly && user?.role !== 'admin') return null;
            return (
              <NavLink key={to} to={to} end={end} onClick={() => setSidebarOpen(false)}>
                <Icon /> <span>{label}</span>
                {badge > 0 && <span className="nav-badge">{badge}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="back-store">
            <FaArrowLeft /> Back to store
          </Link>
          <button onClick={handleLogout} className="logout">
            <FaSignOutAlt /> Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="admin-overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="admin-main">
        <header className="admin-topbar">
          <button className="admin-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <div className="admin-topbar-user">
            <div className="user-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
            <div className="topbar-user-info">
              <strong>{user?.name}</strong>
              <small style={{ textTransform: 'capitalize' }}>{user?.role}</small>
            </div>
          </div>
        </header>

        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
